import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { OutlinePass } from 'three/addons/postprocessing/OutlinePass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { isLiveSceneObject3D } from './assetVisibilityUtils';
import {
  isParticleAnimationRoot,
  resolveParticleAnimationRoot,
} from '../particles/particleAnimationUtils';
import store from '../../redux/store';

const OUTLINE_CONTENT_LAYER = 0;
const OUTLINE_UI_LAYER = 1;
const PARTICLE_OUTLINE_PROXY_NAME = '__particleOutlineProxy';
/**
 * Cap outline proxies. Defaults: snow/rain = 1000 particles, so a low cap left
 * ~half the particles without an outline (even-sampled gaps). Sized to cover the
 * common counts; above this, instances are evenly sampled across the full buffer.
 * The proxy is ONE InstancedMesh (a single OutlinePass target + one draw call),
 * so the only per-count cost is the JS matrix writes — throttled below.
 */
const MAX_PARTICLE_OUTLINE_INSTANCES = 4000;
/** Above this instance count, refresh matrices less often to avoid main-thread spikes. */
const PARTICLE_OUTLINE_HEAVY_INSTANCE_COUNT = 1200;
/** Soft cap on leaf meshes fed to OutlinePass (large GLBs). */
const MAX_OUTLINE_MESH_TARGETS = 64;
let particleProxyRefreshFrame = 0;

let composer = null;
let renderPass = null;
let outlinePass = null;
let outlineRenderCamera = null;
let transformControlsRef = null;
let active = false;

/** Shared geometry/material for Points → InstancedMesh outline proxies. */
let particleOutlineProxyGeometry = null;
let particleOutlineProxyMaterial = null;
const activeParticleOutlineProxies = new Set();

const OUTLINE_SKIP_TYPES = new Set([
  'GridHelper',
  'AxesHelper',
  'CameraHelper',
  'DirectionalLightHelper',
  'PointLightHelper',
  'SpotLightHelper',
  'HemisphereLightHelper',
  'SkeletonHelper',
  'TransformControls',
  'TransformControlsGizmo',
  'TransformControlsPlane',
]);

const _proxyMatrix = new THREE.Matrix4();
const _proxyPosition = new THREE.Vector3();
const _proxyQuaternion = new THREE.Quaternion();
const _proxyScale = new THREE.Vector3();

/** Register transform controls — outline is shown only when selection box is hidden. */
export function registerOutlineTransformControls(transformControls) {
  transformControlsRef = transformControls || null;
}

/** Outline replaces the selection box when showSelectionBox is false (eye-slash / hidden). */
export function shouldUseSelectionOutline() {
  if (!active) return false;
  if (!transformControlsRef) return false;
  return transformControlsRef.showSelectionBox === false;
}

function enableCameraOutlineLayers(camera) {
  if (!camera?.layers) return;
  camera.layers.enable(OUTLINE_CONTENT_LAYER);
  camera.layers.enable(OUTLINE_UI_LAYER);
}

/** UI overlays (gizmo, etc.) render on layer 1 and are excluded from OutlinePass depth/mask. */
export function registerOutlineUiLayerObjects(...roots) {
  roots.filter(Boolean).forEach((root) => {
    root.traverse((child) => {
      child.layers.disable(OUTLINE_CONTENT_LAYER);
      child.layers.enable(OUTLINE_UI_LAYER);
    });
  });
}

function isUnderTransformControls(object) {
  let node = object;
  while (node) {
    if (node.isTransformControls === true) return true;
    if (node.isTransformControlsGizmo === true) return true;
    if (node.isTransformControlsPlane === true) return true;
    if (node.type === 'TransformControls') return true;
    if (node.name === 'transformControls') return true;
    node = node.parent;
  }
  return false;
}

function shouldSkipOutlineObject(object) {
  if (!object) return true;
  if (isUnderTransformControls(object)) return true;
  if (object.isHelper === true) return true;
  if (object.name === 'gridHelper' || object.name === 'transformControls') {
    return true;
  }
  if (object.name === PARTICLE_OUTLINE_PROXY_NAME) return true;
  if (object.userData?.isParticleOutlineProxy) return true;
  if (OUTLINE_SKIP_TYPES.has(object.type)) return true;
  if (object.userData?.isHotspotHelper) return true;
  return false;
}

function getParticleOutlineProxyResources() {
  if (!particleOutlineProxyGeometry) {
    // Very low-poly — thousands of instances must stay cheap for OutlinePass.
    particleOutlineProxyGeometry = new THREE.OctahedronGeometry(0.5, 0);
  }
  if (!particleOutlineProxyMaterial) {
    particleOutlineProxyMaterial = new THREE.MeshBasicMaterial({
      color: 0xff8800,
      // Invisible in the main color pass; OutlinePass swaps materials for the mask.
      colorWrite: false,
      depthWrite: false,
      depthTest: false,
      transparent: true,
      opacity: 0,
    });
  } else {
    particleOutlineProxyMaterial.colorWrite = false;
    particleOutlineProxyMaterial.depthWrite = false;
    particleOutlineProxyMaterial.depthTest = false;
  }
  return {
    geometry: particleOutlineProxyGeometry,
    material: particleOutlineProxyMaterial,
  };
}

function estimatePointsOutlineScale(pointsObj) {
  const mat = pointsObj.material;
  const size =
    (Array.isArray(mat) ? mat[0]?.size : mat?.size) ||
    pointsObj.userData?.size ||
    pointsObj.parent?.userData?.size ||
    0.35;
  // Match Points sprite size more closely so outlines stay visible at range.
  // NOTE: THREE.PointsMaterial renders every sprite at the uniform `material.size`
  // and ignores the per-vertex `size` attribute, so the outline uses this single
  // value for every particle (no per-particle scaling — see writeParticleOutlineInstances).
  return Math.min(6, Math.max(0.25, Number(size) * 1.4 || 0.35));
}

/**
 * Write instance matrices for every particle (or an even sample when over cap).
 * Sampling by stride — not just the first N — so outlines cover the full volume.
 *
 * Every proxy uses the same `baseScale`: standard Points sprites all render at the
 * uniform material size, so scaling proxies by the per-vertex `size` attribute made
 * most outlines too small to detect an edge (only the largest particles showed one).
 */
function writeParticleOutlineInstances(proxy, positions, baseScale) {
  const total = positions.count;
  const outlineCount = Math.min(total, MAX_PARTICLE_OUTLINE_INSTANCES);
  proxy.count = outlineCount;

  _proxyScale.set(baseScale, baseScale, baseScale);

  for (let i = 0; i < outlineCount; i++) {
    const srcIndex =
      total <= outlineCount
        ? i
        : Math.min(total - 1, Math.floor((i * total) / outlineCount));

    _proxyPosition.fromBufferAttribute(positions, srcIndex);
    _proxyMatrix.compose(_proxyPosition, _proxyQuaternion, _proxyScale);
    proxy.setMatrixAt(i, _proxyMatrix);
  }

  proxy.instanceMatrix.needsUpdate = true;
  proxy.computeBoundingSphere?.();
  return outlineCount;
}

/**
 * OutlinePass forces every THREE.Points invisible during the mask pass.
 * Proxies therefore MUST be siblings of Points (not children), or they get
 * culled with the parent and no outline appears for fire/rain/snow/stars.
 */
function ensurePointsOutlineProxy(pointsObj) {
  if (!pointsObj?.isPoints) return null;

  const host = pointsObj.parent;
  if (!host) return null;

  const positions = pointsObj.geometry?.attributes?.position;
  if (!positions || positions.count <= 0) return null;

  const total = positions.count;
  const outlineCount = Math.min(total, MAX_PARTICLE_OUTLINE_INSTANCES);
  const { geometry, material } = getParticleOutlineProxyResources();
  const geometryUuid = pointsObj.geometry?.uuid || null;

  let proxy = pointsObj.userData?.outlineProxy;
  const needsRecreate =
    !proxy ||
    !proxy.isInstancedMesh ||
    proxy.parent !== host ||
    // Capacity too small after particleCount / coreCount increase
    (proxy.instanceMatrix?.array?.length || 0) < outlineCount * 16 ||
    // Geometry was replaced (count/range rebuild) — rebuild proxy instances
    proxy.userData?.sourceGeometryUuid !== geometryUuid;

  if (needsRecreate) {
    if (proxy?.parent) proxy.parent.remove(proxy);
    if (proxy) activeParticleOutlineProxies.delete(proxy);
    proxy = new THREE.InstancedMesh(geometry, material, outlineCount);
    proxy.name = PARTICLE_OUTLINE_PROXY_NAME;
    proxy.userData.isParticleOutlineProxy = true;
    proxy.userData.sourcePoints = pointsObj;
    proxy.userData.sourceGeometryUuid = geometryUuid;
    proxy.frustumCulled = false;
    proxy.matrixAutoUpdate = true;
    proxy.raycast = () => {};
    proxy.layers.set(OUTLINE_CONTENT_LAYER);
    host.add(proxy);
    pointsObj.userData.outlineProxy = proxy;
  }

  proxy.userData.sourceGeometryUuid = geometryUuid;
  proxy.userData.sourceCount = outlineCount;
  proxy.userData.sourceTotal = total;
  proxy.layers.set(OUTLINE_CONTENT_LAYER);
  proxy.visible = true;

  // Match Points local transform so moving fire/rain keeps outline locked on.
  proxy.position.copy(pointsObj.position);
  proxy.quaternion.copy(pointsObj.quaternion);
  proxy.scale.copy(pointsObj.scale);

  writeParticleOutlineInstances(
    proxy,
    positions,
    estimatePointsOutlineScale(pointsObj)
  );

  activeParticleOutlineProxies.add(proxy);
  return proxy;
}

/** Drop outline proxy attached to a Points object (e.g. before fire recreation). */
export function clearOutlineProxyForPoints(pointsObj) {
  if (!pointsObj) return;
  const proxy = pointsObj.userData?.outlineProxy;
  if (!proxy) return;
  proxy.parent?.remove(proxy);
  activeParticleOutlineProxies.delete(proxy);
  delete pointsObj.userData.outlineProxy;
}

/**
 * Rebuild selection outline after particle count/size/shape updates so every
 * particle shape gets an outline again.
 */
export function refreshParticleSelectionOutline() {
  if (!outlinePass || !shouldUseSelectionOutline()) {
    clearParticleOutlineProxies();
    return;
  }
  syncSelectionOutlineFromStore();
}

function clearUnusedParticleOutlineProxies(keep = new Set()) {
  activeParticleOutlineProxies.forEach((proxy) => {
    if (keep.has(proxy)) return;
    const source = proxy.userData?.sourcePoints;
    if (source?.userData?.outlineProxy === proxy) {
      delete source.userData.outlineProxy;
    }
    proxy.parent?.remove(proxy);
    activeParticleOutlineProxies.delete(proxy);
  });
}

export function clearParticleOutlineProxies() {
  clearUnusedParticleOutlineProxies(new Set());
}

/** Collect only asset mesh leaves — never groups, gizmo, or helpers. */
export function resolveOutlineTargets(objects = []) {
  const meshes = [];
  const seen = new Set();
  const keepProxies = new Set();

  const pushMesh = (mesh) => {
    if (!mesh || meshes.length >= MAX_OUTLINE_MESH_TARGETS) return false;
    const key = mesh.uuid;
    if (!key || seen.has(key)) return true;
    seen.add(key);
    meshes.push(mesh);
    return meshes.length < MAX_OUTLINE_MESH_TARGETS;
  };

  objects.filter(isLiveSceneObject3D).forEach((object) => {
    if (meshes.length >= MAX_OUTLINE_MESH_TARGETS) return;
    if (shouldSkipOutlineObject(object)) return;

    const root =
      resolveParticleAnimationRoot(object) ||
      (isParticleAnimationRoot(object) ? object : object);

    const isParticleRoot =
      isParticleAnimationRoot(root) || isParticleAnimationRoot(object);

    // Root itself may be Points (fire) — handle before traverse children.
    if (root.isPoints) {
      const proxy = ensurePointsOutlineProxy(root);
      if (proxy) {
        keepProxies.add(proxy);
        pushMesh(proxy);
      }
      return;
    }

    // Mesh-based particle systems (foliage/clouds/terrain, or a group wrapping
    // Points) can contain hundreds of small meshes — trees, grass blades, bushes.
    // Feeding each leaf blows past MAX_OUTLINE_MESH_TARGETS and drops most of them.
    // OutlinePass traverses every descendant of a selected object itself, so push
    // the whole group ONCE and let it outline all children. Points descendants
    // still need proxies (OutlinePass can't outline THREE.Points).
    if (isParticleRoot) {
      pushMesh(root);
      root.traverse((child) => {
        if (child === root) return;
        if (child.isPoints) {
          const proxy = ensurePointsOutlineProxy(child);
          if (proxy) keepProxies.add(proxy);
        }
      });
      return;
    }

    // Regular assets (GLB, primitives) — collect leaf meshes with the soft cap.
    if (root.isMesh || root.isSkinnedMesh || root.isInstancedMesh) {
      pushMesh(root);
    }

    root.traverse((child) => {
      if (meshes.length >= MAX_OUTLINE_MESH_TARGETS) return;
      if (child === root) return;
      if (shouldSkipOutlineObject(child)) return;

      if (child.isPoints) {
        const proxy = ensurePointsOutlineProxy(child);
        if (proxy) {
          keepProxies.add(proxy);
          pushMesh(proxy);
        }
        return;
      }

      if (!child.isMesh && !child.isSkinnedMesh && !child.isInstancedMesh) {
        return;
      }

      pushMesh(child);
    });
  });

  clearUnusedParticleOutlineProxies(keepProxies);
  return meshes;
}

function syncOutlineOnlyCamera(sourceCamera) {
  if (!sourceCamera) return;

  if (
    !outlineRenderCamera ||
    outlineRenderCamera.type !== sourceCamera.type
  ) {
    outlineRenderCamera = sourceCamera.clone();
  }

  outlineRenderCamera.position.copy(sourceCamera.position);
  outlineRenderCamera.quaternion.copy(sourceCamera.quaternion);
  outlineRenderCamera.scale.copy(sourceCamera.scale);
  outlineRenderCamera.up.copy(sourceCamera.up);

  if (sourceCamera.isPerspectiveCamera && outlineRenderCamera.isPerspectiveCamera) {
    outlineRenderCamera.fov = sourceCamera.fov;
    outlineRenderCamera.aspect = sourceCamera.aspect;
    outlineRenderCamera.near = sourceCamera.near;
    outlineRenderCamera.far = sourceCamera.far;
    outlineRenderCamera.focus = sourceCamera.focus;
    outlineRenderCamera.filmGauge = sourceCamera.filmGauge;
    outlineRenderCamera.filmOffset = sourceCamera.filmOffset;
  } else if (
    sourceCamera.isOrthographicCamera &&
    outlineRenderCamera.isOrthographicCamera
  ) {
    outlineRenderCamera.left = sourceCamera.left;
    outlineRenderCamera.right = sourceCamera.right;
    outlineRenderCamera.top = sourceCamera.top;
    outlineRenderCamera.bottom = sourceCamera.bottom;
    outlineRenderCamera.near = sourceCamera.near;
    outlineRenderCamera.far = sourceCamera.far;
    outlineRenderCamera.zoom = sourceCamera.zoom;
  }

  outlineRenderCamera.layers.set(OUTLINE_CONTENT_LAYER);
  outlineRenderCamera.updateProjectionMatrix();
  outlineRenderCamera.updateMatrixWorld(true);
}

export function initSelectionOutline(renderer, scene, camera) {
  if (!renderer || !scene || !camera || composer) {
    return { composer, outlinePass };
  }

  enableCameraOutlineLayers(camera);

  const size = new THREE.Vector2();
  renderer.getSize(size);

  composer = new EffectComposer(renderer);
  renderPass = new RenderPass(scene, camera);
  composer.addPass(renderPass);

  outlinePass = new OutlinePass(
    new THREE.Vector2(size.x, size.y),
    scene,
    camera
  );
  outlinePass.edgeStrength = 5;
  outlinePass.edgeGlow = 0;
  outlinePass.edgeThickness = 1.2;
  outlinePass.pulsePeriod = 0;
  outlinePass.visibleEdgeColor.set('#ff8800');
  outlinePass.hiddenEdgeColor.set('#442200');
  composer.addPass(outlinePass);
  composer.addPass(new OutputPass());

  syncOutlineOnlyCamera(camera);
  if (outlineRenderCamera) {
    outlinePass.renderCamera = outlineRenderCamera;
  }

  active = true;
  return { composer, outlinePass };
}

export function isSelectionOutlineActive() {
  return active && !!composer;
}

export function setSelectionOutlineEnabled(enabled) {
  active = !!enabled;
  if (!active && outlinePass) {
    outlinePass.selectedObjects = [];
    clearParticleOutlineProxies();
  }
}

export function syncSelectionOutlineCamera(camera) {
  if (!camera) return;
  enableCameraOutlineLayers(camera);
  if (renderPass) renderPass.camera = camera;
  syncOutlineOnlyCamera(camera);
  if (outlinePass && outlineRenderCamera) {
    outlinePass.renderCamera = outlineRenderCamera;
  }
}

export function resizeSelectionOutline(width, height) {
  if (!composer || !width || !height) return;
  composer.setSize(width, height);
}

export function updateSelectionOutlineObjects(objects = []) {
  if (!outlinePass) return;
  outlinePass.selectedObjects = resolveOutlineTargets(objects);
}

/** Sync outline strictly from current Redux selection (no stale React state). */
export function syncSelectionOutlineFromStore(state = store.getState()) {
  if (!outlinePass) return;

  if (
    state?.window?.window_type === 'preview' ||
    !shouldUseSelectionOutline()
  ) {
    outlinePass.selectedObjects = [];
    clearParticleOutlineProxies();
    return;
  }

  const materialHighlight = (
    state?.assets_object?.materialHighlightMeshes ?? []
  ).filter(isLiveSceneObject3D);
  if (materialHighlight.length > 0) {
    outlinePass.selectedObjects = resolveOutlineTargets(materialHighlight);
    return;
  }

  const multi = (state?.assets_object?.selectedMeshesForGrouping ?? []).filter(
    isLiveSceneObject3D
  );
  const primary = state?.assets_object?.select_object;
  const livePrimary = isLiveSceneObject3D(primary) ? primary : null;

  const objects =
    multi.length > 0 ? multi : livePrimary ? [livePrimary] : [];

  outlinePass.selectedObjects = resolveOutlineTargets(objects);
}

function refreshActiveParticleOutlineProxies(force = false) {
  // Avoid rewriting thousands of instance matrices every RAF — that freezes selection.
  // Heavier systems (snow/rain at 1000+) refresh on a slower cadence.
  particleProxyRefreshFrame += 1;

  activeParticleOutlineProxies.forEach((proxy) => {
    const pointsObj = proxy.userData?.sourcePoints;
    if (!pointsObj?.isPoints || !pointsObj.parent) return;

    if (!force) {
      const heavy =
        (proxy.userData?.sourceCount || 0) >=
        PARTICLE_OUTLINE_HEAVY_INSTANCE_COUNT;
      const stride = heavy ? 8 : 4;
      if (particleProxyRefreshFrame % stride !== 0) return;
    }

    ensurePointsOutlineProxy(pointsObj);
  });
}

export function syncMaterialHighlightVisuals(state = store.getState()) {
  syncSelectionOutlineFromStore(state);
  if (transformControlsRef?.refreshSelectionBounds) {
    transformControlsRef.refreshSelectionBounds();
  }
}

export function clearSelectionOutline() {
  if (outlinePass) {
    outlinePass.selectedObjects = [];
  }
  clearParticleOutlineProxies();
}

export function renderSelectionOutline() {
  if (!shouldUseSelectionOutline() || !composer) return false;
  refreshActiveParticleOutlineProxies();
  composer.render();
  return true;
}

export function disposeSelectionOutline() {
  clearParticleOutlineProxies();
  if (outlinePass) {
    outlinePass.selectedObjects = [];
  }
  composer?.dispose();
  composer = null;
  renderPass = null;
  outlinePass = null;
  outlineRenderCamera = null;
  transformControlsRef = null;
  active = false;

  particleOutlineProxyGeometry?.dispose?.();
  particleOutlineProxyMaterial?.dispose?.();
  particleOutlineProxyGeometry = null;
  particleOutlineProxyMaterial = null;
}

/** Render the main viewport with outline post-processing when active. */
export function renderViewportWithSelectionOutline(renderer, scene, camera) {
  if (!renderer || !scene || !camera) return false;

  if (isSelectionOutlineActive() && shouldUseSelectionOutline()) {
    syncSelectionOutlineCamera(camera);
    if (outlinePass?.selectedObjects?.length) {
      refreshActiveParticleOutlineProxies();
      renderSelectionOutline();
      return true;
    }
  }

  if (outlinePass) {
    outlinePass.selectedObjects = [];
  }
  clearParticleOutlineProxies();

  if (typeof renderer.clear === 'function') {
    renderer.clear();
  }
  renderer.render(scene, camera);
  return true;
}
